import {
  CancelOutlined,
  Close,
  ExpandMore,
  History,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../service/Api";
import { tokens } from "../../theme";
import { ContactIcon } from "../../layout/sidebar/icons/ContactIcon";
import { BookingsIcon } from "../../layout/sidebar/icons/BookingsIcon";
import { LocationIcon } from "../icons";
import { OperationsIcon } from "../../layout/sidebar/icons";

export type SearchEntityType = "booking" | "stream" | "venue" | "contact";

export interface SearchResult {
  id: number;
  type: SearchEntityType;
  title: string;
  subtitle: string;
  url: string;
  model: string;
  date?: string;
  status?: "confirmed" | "pending" | "cancelled";
  venue?: string;
  phone?: string;
  email?: string;
  address?: string;
  capacity?: number;
  parent?: { id: number; name: string } | null;
}

interface GlobalSearchBarProps {
  compact?: boolean;
  size?: "small" | "medium";
  placeholder?: string;
}

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  confirmed: { bg: "#E6F4EA", color: "#2E7D32" },
  pending: { bg: "#FFF8E1", color: "#B8860B" },
  cancelled: { bg: "#FDECEA", color: "#C62828" },
};

const StatusBadge = ({ status }: { status: string }) => {
  const { bg, color } = STATUS_STYLES[status] ?? {
    bg: "#f5f5f5",
    color: "#555",
  };
  return (
    <Box
      component="span"
      sx={{
        backgroundColor: bg,
        color,
        fontSize: "0.7rem",
        fontWeight: 500,
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        textTransform: "capitalize",
        flexShrink: 0,
      }}
    >
      {status}
    </Box>
  );
};

const RECENT_KEY = "globalSearchRecent";
const MAX_RECENT = 5;

function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
}
function saveRecent(query: string) {
  const updated = [query, ...loadRecent().filter((s) => s !== query)].slice(
    0,
    MAX_RECENT,
  );
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
}
function removeRecent(query: string) {
  localStorage.setItem(
    RECENT_KEY,
    JSON.stringify(loadRecent().filter((s) => s !== query)),
  );
}

const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  compact = false,
  placeholder = "Search bookings, streams, venues, contacts…",
  size = "medium",
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [filterType, setFilterType] = useState<SearchEntityType | "all">("all");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SearchResult | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>(loadRecent);
  const [compactExpanded, setCompactExpanded] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchResults = useCallback(
    async (q: string, type: SearchEntityType | "all") => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const params: Record<string, string> = { q: q.trim() };
        if (type !== "all") params.type = type;
        const res = await apiService.get<SearchResult[]>(
          "/api/management/global-search/",
          params,
        );
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setViewAll(false);

    debounceRef.current = setTimeout(
      () => fetchResults(query, filterType),
      300,
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, filterType, fetchResults]);

  const handleFocus = () => {
    setOpen(true);
    setRecentSearches(loadRecent());
  };
  const handleClickAway = () => {
    setOpen(false);
    setViewAll(false);
    if (compact) setCompactExpanded(false);
  };
  const handleSelectResult = (result: SearchResult) => {
    setSelectedItem(result);
    saveRecent(result.title);
    setRecentSearches(loadRecent());
    setQuery("");
    setResults([]);
    navigate(result.parent ? `/venues/view/${result.parent.id}` : result.url);
  };
  const handleClearSelected = () => {
    setSelectedItem(null);
    setQuery("");
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleRecentClick = (term: string) => {
    setQuery(term);
    setOpen(true);
    fetchResults(term, filterType);
    setTimeout(() => inputRef.current?.focus(), 50);
  };
  const handleRemoveRecent = (term: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeRecent(term);
    setRecentSearches(loadRecent());
  };

  const grouped: Record<SearchEntityType, SearchResult[]> = {
    contact: [],
    booking: [],
    venue: [],
    stream: [],
  };
  for (const r of results) grouped[r.type]?.push(r);

  const hasQuery = !!query.trim();
  const hasResults = results.length > 0;
  const allColumns = [
    "contact",
    "booking",
    "venue",
    "stream",
  ] as SearchEntityType[];
  const visibleColumns = hasQuery
    ? allColumns.filter((t) => grouped[t].length > 0)
    : allColumns;

  const showRecentPanel = open && !hasQuery && recentSearches.length > 0;
  const showResultsPanel = open && hasQuery && (hasResults || loading);
  const showEmptyPanel = open && hasQuery && !loading && !hasResults;
  const showPanel = showRecentPanel || showResultsPanel || showEmptyPanel;

  const primaryText = {
    fontWeight: 600,
    color: colors.primary[100],
    fontSize: "0.78rem",
    lineHeight: 1.3,
  };

  const secondaryText = {
    color: colors.primary[400],
    fontSize: "0.68rem",
    lineHeight: 1.35,
  };

  const rowBox = {
    borderRadius: "8px",
    px: 1,
    py: 0.75,
    cursor: "pointer",
    "&:hover": { backgroundColor: colors.primary[800] },
  };

  const borderColor =
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.15)"
      : "rgba(0,0,0,0.15)";

  const columnIcons: Record<SearchEntityType, React.ReactNode> = {
    contact: <ContactIcon sx={{ color: colors.primary[400], fontSize: 18 }} />,
    booking: <BookingsIcon sx={{ color: colors.primary[400], fontSize: 18 }} />,
    venue: <LocationIcon sx={{ color: colors.primary[400], fontSize: 18 }} />,
    stream: (
      <OperationsIcon sx={{ color: colors.primary[400], fontSize: 18 }} />
    ),
  };

  const columnLabels: Record<SearchEntityType, string> = {
    contact: "CONTACTS",
    booking: "BOOKINGS",
    venue: "VENUES",
    stream: "STREAMS",
  };

  const PREVIEW = 4;

  const renderContacts = () => {
    const items = viewAll ? grouped.contact : grouped.contact.slice(0, PREVIEW);
    if (items.length === 0) return null;
    return (
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {items.map((c) => (
          <Box
            key={c.id}
            onClick={() => handleSelectResult(c)}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              ...rowBox,
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: "0.8rem",
                backgroundColor: colors.primary[400],
                flexShrink: 0,
              }}
            >
              {c.title?.charAt(0)?.toUpperCase() || "?"}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={primaryText}>{c.title}</Typography>
              {c.subtitle && (
                <Typography sx={secondaryText}>{c.subtitle}</Typography>
              )}
              {c.phone && <Typography sx={secondaryText}>{c.phone}</Typography>}
              {c.email && <Typography sx={secondaryText}>{c.email}</Typography>}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderBookings = () => {
    const items = viewAll ? grouped.booking : grouped.booking.slice(0, PREVIEW);
    if (items.length === 0) return null;
    return (
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {items.map((b) => (
          <Box key={b.id} onClick={() => handleSelectResult(b)} sx={rowBox}>
            <Typography sx={primaryText}>{b.title}</Typography>
            {(b.date || b.status) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 0.4,
                  flexWrap: "wrap",
                }}
              >
                {b.date && <Typography sx={secondaryText}>{b.date}</Typography>}
                {b.status && <StatusBadge status={b.status} />}
              </Box>
            )}
            {b.subtitle && (
              <Typography sx={{ ...secondaryText, mt: 0.4 }}>
                {b.subtitle}
              </Typography>
            )}
            {b.venue && <Typography sx={secondaryText}>{b.venue}</Typography>}
          </Box>
        ))}
      </Box>
    );
  };

  const renderVenues = () => {
    const items = (
      viewAll ? grouped.venue : grouped.venue.slice(0, PREVIEW)
    ).filter((v) => !v.parent);
    if (items.length === 0) return null;
    return (
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {items.map((v) => (
          <Box key={v.id} onClick={() => handleSelectResult(v)} sx={rowBox}>
            <Typography sx={primaryText}>{v.title}</Typography>
            {v.subtitle && (
              <Typography sx={secondaryText}>{v.subtitle}</Typography>
            )}
            {v.address && (
              <Typography sx={secondaryText}>{v.address}</Typography>
            )}
            {v.capacity && (
              <Typography sx={secondaryText}>Capacity: {v.capacity}</Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderStreams = () => {
    const items = viewAll ? grouped.stream : grouped.stream.slice(0, PREVIEW);
    if (items.length === 0) return null;
    return (
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {items.map((s) => (
          <Box key={s.id} onClick={() => handleSelectResult(s)} sx={rowBox}>
            <Typography sx={primaryText}>{s.title}</Typography>
            {s.date && <Typography sx={secondaryText}>{s.date}</Typography>}
            {s.subtitle && (
              <Typography sx={secondaryText}>{s.subtitle}</Typography>
            )}
            {s.status && (
              <Box sx={{ mt: 0.5 }}>
                <StatusBadge status={s.status} />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const columnRenderers: Record<SearchEntityType, () => React.ReactNode> = {
    contact: renderContacts,
    booking: renderBookings,
    venue: renderVenues,
    stream: renderStreams,
  };

  if (compact && !compactExpanded) {
    return (
      <IconButton
        aria-label="Global search"
        size="small"
        onClick={() => {
          setCompactExpanded(true);
          setOpen(true);
          setRecentSearches(loadRecent());
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        sx={{
          borderRadius: "50%",
          border: `1px solid ${borderColor}`,
          backgroundColor: "transparent",
          width: 40,
          height: 40,
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          position: "relative",
          width: compact ? 320 : "100%",
          maxWidth: compact ? 320 : 560,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            border: `2px solid ${open ? theme.palette.primary.main : borderColor}`,
            borderRadius: "50px",
            px: 1.5,
            py: compact ? 0.5 : size === "small" ? 0.75 : 1.5,
            backgroundColor: colors.primary[900],
            transition: "border-color 0.2s",
            gap: 1,
          }}
        >
          <Select
            value={filterType}
            onChange={(e) =>
              setFilterType(e.target.value as SearchEntityType | "all")
            }
            size="small"
            variant="standard"
            disableUnderline
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: colors.primary[400],
              minWidth: compact ? 68 : 80,
              "& .MuiSelect-select": { py: 0, pr: "20px !important" },
              "& .MuiSvgIcon-root": { fontSize: 16 },
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="booking">Bookings</MenuItem>
            <MenuItem value="stream">Streams</MenuItem>
            <MenuItem value="venue">Venues</MenuItem>
            <MenuItem value="contact">Contacts</MenuItem>
          </Select>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {selectedItem && (
            <Chip
              size="small"
              label={selectedItem.title}
              onDelete={handleClearSelected}
              deleteIcon={<CancelOutlined sx={{ fontSize: 16 }} />}
              sx={{
                backgroundColor: colors.primary[700] + "22",
                color: colors.primary[400],
                fontWeight: 600,
                fontSize: "0.75rem",
                flexShrink: 0,
                maxWidth: 150,
              }}
            />
          )}

          <TextField
            fullWidth
            inputRef={inputRef}
            placeholder={selectedItem ? "Search more…" : placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleFocus}
            variant="standard"
            autoComplete="off"
            inputProps={{
              autoCorrect: "off",
              autoCapitalize: "off",
              spellCheck: "false",
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={18} sx={{ mx: 1 }} />
                  ) : query ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setQuery("");
                        setResults([]);
                      }}
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : (
                    <SearchIcon
                      sx={{ color: colors.primary[500], fontSize: 22, mx: 0.5 }}
                    />
                  )}
                </InputAdornment>
              ),
              disableUnderline: true,
              sx: {
                fontSize: compact ? "0.875rem" : "1rem",
                color: colors.primary[100],
                "& ::placeholder": { color: colors.primary[500], opacity: 1 },
              },
            }}
          />

          {compact && (
            <IconButton size="small" onClick={handleClickAway}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Paper>

        {showPanel && (
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              top: "calc(100% + 12px)",
              width: {
                xs: "calc(100vw - 32px)",
                sm: "600px",
                md: "800px",
                lg: "950px",
              },
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1300,
              borderRadius: { xs: "16px", md: "24px" },
              border: `1px solid ${colors.primary[400]}40`,
              backgroundColor: colors.primary[900],
              maxHeight: { xs: 360, md: 416 },
              minHeight: 200,
              overflowY: "auto",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
            }}
          >
            {/* Recent searches */}
            {!loading && showRecentPanel && (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.primary[400],
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    fontSize: "0.85rem",
                    mb: 2,
                  }}
                >
                  Recent Searches
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {recentSearches.map((term, i) => (
                    <Box
                      key={`${term}-${i}`}
                      onClick={() => handleRecentClick(term)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        ...rowBox,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          minWidth: 0,
                        }}
                      >
                        <History
                          sx={{ color: colors.primary[400], fontSize: 18 }}
                        />
                        <Typography
                          sx={{
                            color: colors.primary[400],
                            fontSize: "0.9rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {term}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleRemoveRecent(term, e)}
                        sx={{ color: colors.primary[400] }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {!loading && showEmptyPanel && (
              <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
                <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                  No results found for <strong>&quot;{query}&quot;</strong>
                </Typography>
              </Box>
            )}

            {!loading && showResultsPanel && visibleColumns.length > 0 && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm:
                        visibleColumns.length > 2
                          ? "repeat(2, 1fr)"
                          : `repeat(${visibleColumns.length}, 1fr)`,
                      md: `repeat(${visibleColumns.length}, 1fr)`,
                    },
                    gap: { xs: 2, md: 0 },
                    "& > *:not(:last-child)": {
                      borderRight: { md: `1px solid ${colors.primary[400]}40` },
                      borderBottom: {
                        xs: `1px solid ${colors.primary[400]}40`,
                        md: "none",
                      },
                      pb: { xs: 2, md: 0 },
                    },
                    pt: { xs: 2, md: 3 },
                  }}
                >
                  {visibleColumns.map((type) => (
                    <Box key={type} sx={{ px: { xs: 2, md: 3 } }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {columnIcons[type]}
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: colors.primary[400],
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            fontSize: "0.75rem",
                          }}
                        >
                          {columnLabels[type]}
                        </Typography>
                      </Box>
                      {columnRenderers[type]()}
                    </Box>
                  ))}
                </Box>

                <Box
                  onClick={() => setViewAll(!viewAll)}
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-end" }, // Centered on mobile
                    alignItems: "center",
                    px: { xs: 2, md: 3 },
                    py: 2,
                    cursor: "pointer",
                    borderTop: { xs: "none", md: `1px solid transparent` },
                  }}
                >
                  <Typography
                    sx={{
                      color: colors.primary[400],
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      "&:hover": { color: colors.primary[100] },
                      transition: "color 0.2s",
                    }}
                  >
                    {viewAll ? "View less" : "View all"}
                  </Typography>
                  <ExpandMore
                    sx={{
                      color: colors.primary[400],
                      fontSize: 18,
                      transform: viewAll ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      ml: 0.5,
                    }}
                  />
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearchBar;
